import React, { useLayoutEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { REGIONAL_COLORS, BLACK, WHITE, __am5_chart_license__ } from "../../../constants";

type Datum = {
    date: string; // yyyy-mm-dd
    [k: string]: string | number | null;
};

type SeriesDef = {
    name: string; // e.g. "Nissan",
    field: string; // e.g. "nissan"
};

type Props = {
    chartId: string;
    data: Datum[];
    series: SeriesDef[];
    height?: number;
    theme?: "dark" | "light";
    showPercentage?: boolean;
};

const PALETTE = REGIONAL_COLORS.filter(
    (c) =>
        c &&
        c.toLowerCase() !== "rgb(255,255,255)" &&
        c.toLowerCase() !== "rgba(0,0,0,0)" &&
        c.toLowerCase() !== "#a4a4a4"
);



function baseKey(nameOrField: string) {
    return nameOrField
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
}

function buildColorMap(series: SeriesDef[]) {
    const map = new Map<string, string>();
    let i = 0;
    for (const s of series) {
        const b = baseKey(s.name) || baseKey(s.field);
        if (!map.has(b)) {
            map.set(b, PALETTE[i % PALETTE.length] ?? "#64748b");
            i += 1;
        }
    }
    return map;
}

export default function EvolutionOverTimeChart({
    chartId,
    data,
    series,
    height = 300,
    theme = "dark",
    showPercentage = false,
}: Props) {
    useLayoutEffect(() => {

        if (!chartId) return;

        const text = theme === "dark" ? WHITE : BLACK;

        am5.addLicense(__am5_chart_license__);
        const root = am5.Root.new(chartId);
        root.setThemes([am5themes_Animated.new(root)]);

        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "none",
                wheelY: "none",
                layout: root.verticalLayout,
            })
        );

        const parsed = (data ?? []).map((d) => ({
            ...d,
            dateTS: new Date(`${d.date}T00:00:00`).getTime(),
        }));

        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: { timeUnit: "day", count: 1 },
                renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 40 }),
                tooltip: am5.Tooltip.new(root, {}),
            })
        );

        xAxis.get("renderer").labels.template.setAll({
            fill: am5.color(text),
            fontSize: "12px",
        });
        xAxis.get("renderer").grid.template.set("stroke", am5.color(text));

        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {}),
                min: 0,
            })
        );

        yAxis.get("renderer").labels.template.setAll({
            fill: am5.color(text),
            fontSize: "12px",
            paddingRight: 4,
        });
        yAxis.get("renderer").setAll({ minWidth: 30 });
        yAxis.get("renderer").labels.template.adapters.add("text", (val) => val ? (showPercentage ? `${val}%` : val) : val);
        yAxis.get("renderer").grid.template.set("stroke", am5.color(text));

        yAxis.children.unshift(
            am5.Label.new(root, {
                rotation: -90,
                text: showPercentage ? "SoV" : "Position",
                y: am5.percent(50),
                centerX: am5.percent(50),
                fill: am5.color(text),
                fontSize: "12px",
            })
        );

        // Cursor without a shared tooltip — just drives cross-hair
        const cursor = chart.set(
            "cursor",
            am5xy.XYCursor.new(root, { behavior: "none" })
        );
        cursor.lineY.set("visible", false);

        const colorMap = buildColorMap(series);
        const allSeries: am5xy.LineSeries[] = [];

        for (const s of series) {


            const b = baseKey(s.name) || baseKey(s.field);
            const colorStr = colorMap.get(b) ?? (PALETTE[0] || "#64748b");
            const seriesColor = am5.color(colorStr);

            const tooltip = am5.Tooltip.new(root, {
                getFillFromSprite: false,
                getStrokeFromSprite: false,
                autoTextColor: false,
                pointerOrientation: "horizontal",
            });

            tooltip.get("background")?.setAll({
                fill: am5.color(0x1e293b),
                fillOpacity: 0.92,
                stroke: seriesColor,
                strokeWidth: 1,
            });

            const line = chart.series.push(
                am5xy.LineSeries.new(root, {
                    name: s.name,
                    xAxis,
                    yAxis,
                    valueXField: "dateTS",
                    valueYField: s.field,
                    connect: true,
                    stroke: seriesColor,
                    tooltip,
                })
            );


            line.strokes.template.setAll({
                strokeWidth: 2,
                strokeOpacity: 1,
            });

            // Bullet color always matches the line color
            line.bullets.push(() => {
                const circle = am5.Circle.new(root, {
                    radius: 3,
                    fillOpacity: 1,
                    strokeWidth: 1,
                });
                circle.adapters.add("fill", () => line.get("stroke"));
                circle.adapters.add("stroke", () => line.get("stroke"));
                return am5.Bullet.new(root, { sprite: circle });
            });

            line.data.setAll(parsed);
            allSeries.push(line);
        }

        // Each series tooltip shows only its own value.
        for (const hoveredLine of allSeries) {
            hoveredLine.get("tooltip")?.label.adapters.add("text", (_text, _target) => {
                const di = hoveredLine.get("tooltipDataItem");
                if (!di) return "";

                const dateStr = ((di.dataContext as Datum)?.date) ?? "";

                const v = di.get("valueY");
                const label = hoveredLine.get("name") ?? "";

                return [
                    `[bold][#ffffff][fontSize: 12px]${dateStr}[/][/][/]`,
                    `[#ffffff][fontSize: 12px]${label}: ${typeof v === "number" ? v.toFixed(1) + (showPercentage ? "%" : "") : "—"}[/][/]`,
                ].join("\n");
            });
        }

        xAxis.data.setAll(parsed);

        const legend = chart.children.push(
            am5.Legend.new(root, {
                centerX: am5.percent(50),
                x: am5.percent(50),
                paddingTop: 10,
            })
        );
        legend.labels.template.setAll({ fill: am5.color(text), fontSize: "12px" });
        legend.data.setAll(allSeries);

        chart.appear(800, 100);

        return () => root.dispose();
    }, [chartId, data, series, theme]);

    return <div id={chartId} style={{ width: "100%", height }} />;
}